/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { apiRequest, getToken, setToken, type AuthUser } from '@/lib/auth';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  register: (name: string, email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    apiRequest<{ user: AuthUser }>('/api/auth/me', {}, token)
      .then((result) => setUser(result.user))
      .catch(() => setToken(null))
      .finally(() => setLoading(false));
  }, []);

  const announce = () => window.dispatchEvent(new Event('partitura-auth-changed'));

  const register = useCallback(async (name: string, email: string, password: string) => {
    const result = await apiRequest<{ token: string; user: AuthUser }>(
      '/api/auth/register',
      { method: 'POST', body: JSON.stringify({ name, email, password }) },
      null,
    );
    setToken(result.token);
    setUser(result.user);
    announce();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await apiRequest<{ token: string; user: AuthUser }>(
      '/api/auth/login',
      { method: 'POST', body: JSON.stringify({ email, password }) },
      null,
    );
    setToken(result.token);
    setUser(result.user);
    announce();
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiRequest('/api/auth/logout', { method: 'POST', body: '{}' });
    } catch {
      // encerra localmente mesmo se a rede falhar
    }
    setToken(null);
    try {
      window.localStorage.removeItem('ler-na-clave:progress:v2');
      window.localStorage.removeItem('ler-na-clave:progress:v1');
      window.localStorage.removeItem('ler-na-clave:attempts:v1');
    } catch {
      // sem armazenamento local
    }
    setUser(null);
    announce();
  }, []);

  const value = useMemo(
    () => ({ user, loading, register, login, logout }),
    [user, loading, register, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth precisa estar dentro de AuthProvider');
  return value;
}
